import { useEffect } from 'react';
import { generatePath, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { Box, Breadcrumbs, Card, CardContent, Link, Table, TableBody, useTheme } from '@mui/material';
import _ from 'lodash';

import { usePrevious } from 'common/hooks';
import { breadcrumbIconSx, breadcrumbLinkSx, breadcrumbsContainerSx, breadcrumbsSx } from 'common/styles';

import { DetailsCardTitle } from 'commonComponents/DetailsCardTitle';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';
import { mapBoolean } from 'commonComponents/MaterialTable';
import { PageContainer } from 'commonComponents/PageContainer';
import { TableRow } from 'commonComponents/TableRow';

import { useLicense, useLicenseFile } from 'api/licenses';
import { useCurrentProduct } from 'api/products';
import { UndoIcon } from 'icons';
import { licenseDetailBackContext } from 'licenses/LicenseDetails/LicenseDetailsContext';
import {
    creationDate,
    getValueOrFallback,
    hasInficryptSystemId,
    hasMacAddress,
    hasNoneHardwareBinding,
    hasSmartDrLegacySiteId,
    hasSystemId,
    licenseFileExpirationDate,
    licenseGenerateCanSignFiles,
    licenseGenerateSupportsMultiplePlatforms,
    PLATFORMS,
} from 'licenses/utils';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

import { LicenseCollapse } from './LicenseCollapse';
import { LicenseFileActions } from './LicenseFileActions/LicenseFileActions';
import { LicenseFileHistory } from './LicenseFileHistory';

export function LicenseFileDetails() {
    const { id: licenseFileId } = useParams<{ id: string }>();
    const { data: currentProduct } = useCurrentProduct();
    const previousProduct = usePrevious(currentProduct);
    const navigate = useNavigate();
    const theme = useTheme();

    const { data: licenseFile, error: licenseFileError } = useLicenseFile(Number(licenseFileId));
    const { data: license, error: licenseError } = useLicense(licenseFile?.license, Boolean(licenseFile));

    useEffect(() => {
        if (previousProduct && !_.isEqual(previousProduct, currentProduct)) {
            navigate(ROUTES.LICENSE_LIST);
        }
    }, [currentProduct, previousProduct, navigate]);

    if (licenseFileError || licenseError) {
        return null;
    }

    if (!licenseFile || !license) {
        return <LoadingIndicator />;
    }

    const { configuration: productConfiguration } = license;

    return (
        <PageContainer
            maxWidth="lg"
            sx={{
                '& .MuiContainer-root': {
                    paddingTop: 0,
                },
            }}
        >
            <Box sx={breadcrumbsContainerSx}>
                <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbsSx}>
                    {licenseDetailBackContext.map(context => (
                        <Link
                            key={context.url}
                            component={RouterLink}
                            to={context.url}
                            underline="hover"
                            data-testid="LicenseFileDetails_BreadcrumbLicenses"
                            sx={breadcrumbLinkSx}
                        >
                            <UndoIcon sx={breadcrumbIconSx} />
                            {context.label}
                        </Link>
                    ))}
                    <Link
                        component={RouterLink}
                        to={generatePath(ROUTES.LICENSE_DETAIL, { id: licenseFile.license.toString() })}
                        underline="hover"
                        data-testid="LicenseFileDetails_BreadcrumbLicenseDetails"
                        sx={breadcrumbLinkSx}
                    >
                        <UndoIcon sx={breadcrumbIconSx} />
                        License Details
                    </Link>
                </Breadcrumbs>
            </Box>
            <LicenseCollapse license={license} />

            <Card style={{ marginTop: theme.spacing(1) }}>
                <CardContent data-testid="LicenseFileDetails_CardContent">
                    <DetailsCardTitle
                        subtitle={`License File${licenseFile.isExpired ? ' [EXPIRED]' : ''}`}
                        showError={licenseFile.isExpired}
                    />
                    <Table size="small" style={{ whiteSpace: 'nowrap' }}>
                        <TableBody>
                            <HardwareIdentification config={productConfiguration} file={licenseFile} />
                            <TableRow
                                title="Activation Date"
                                value={creationDate(licenseFile)}
                                testId="activation_date"
                            />
                            <TableRow
                                title="Activated by"
                                value={getValueOrFallback(licenseFile.creator_fullname, licenseFile.creator)}
                                testId="activated_by"
                            />
                            <TableRow
                                title="Activation Expiration"
                                value={licenseFileExpirationDate(licenseFile.expiration_date)}
                                testId="expiration_date"
                            />
                            <TableRow
                                title="Support Expiration"
                                value={licenseFileExpirationDate(licenseFile.support_expiration_date)}
                                testId="support_expiration_date"
                            />
                            <TableRow title="Comment" value={licenseFile.comment} testId="comment" />
                            <TableRow title="Computer Name" value={licenseFile.computer_name} testId="computer_name" />
                            {licenseGenerateSupportsMultiplePlatforms(productConfiguration) && (
                                <TableRow
                                    title="Platforms"
                                    value={licenseFile.platforms
                                        .split(' ')
                                        .map(p => PLATFORMS[p])
                                        .join(' ')}
                                    testId="platforms"
                                />
                            )}
                            {licenseFile.hardwareBinding?.type === 'Inficrypt System Id' && (
                                <TableRow
                                    title="License Identifier"
                                    value={licenseFile.license_identifier}
                                    testId="license_identifier"
                                />
                            )}
                            {licenseGenerateCanSignFiles(productConfiguration) && (
                                <TableRow
                                    title="License can sign files"
                                    value={mapBoolean(licenseFile.license_can_sign_files)}
                                    testId="can_sign_files"
                                />
                            )}
                            <TableRow title="File Name" value={licenseFile.filename} testId="file_name" />
                        </TableBody>
                    </Table>
                </CardContent>
                <LicenseFileActions licenseFile={licenseFile} license={license} />
            </Card>
            <RoleDependentComponent
                userRole={ROLE_DEFINITIONS.LicenseManager}
                component={LicenseFileHistory}
                licenseFileId={Number(licenseFileId)}
            />
        </PageContainer>
    );
}

function HardwareIdentification({ config, file }) {
    if (hasNoneHardwareBinding(config)) return null;
    let title = '';

    if (hasSystemId(config) || hasInficryptSystemId(config)) title = 'System ID';
    if (hasMacAddress(config)) title = 'MAC Addresses';
    if (hasSmartDrLegacySiteId(config)) title = 'Site ID';
    return <TableRow title={title} value={file.hardwareIdentifiers.join(', ')} testId="hardwareIdentification" />;
}
